import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Check,
  ChevronRight,
  Copy,
  User,
  UserPlus,
  UserX,
  X,
  Trash2,
  Edit2,
  Save,
  Archive,
  LogOut,
  BookOpen,
  StickyNote,
  Layers,
  MessageSquareText,
  Users,
  Shield,
  Lock,
  Globe,
  KeyRound,
  EyeOff,
} from "lucide-react";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import CommunityAvatar from "../components/CommunityAvatar";
import { communitiesAPI } from "../services/api";
import { useAuthState } from "../hooks/useAuth";
import { useToasts } from "../hooks/useToasts";
import { formatReadingEstimate } from "../utils/readingTime";
import "./StudyLessons.css";
import "./CommunityDetails.css";

const CommunityDetails = () => {
  const { communityId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { user } = useAuthState();
  const communitiesSnapshot = location.state?.fromCommunitiesSnapshot || null;
  const prefetchedCommunity = location.state?.prefetchedCommunity || null;
  const [community, setCommunity] = useState(prefetchedCommunity);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(!prefetchedCommunity);
  const [shareCode, setShareCode] = useState("");
  const [isEditingCommunity, setIsEditingCommunity] = useState(false);
  const [communityNameInput, setCommunityNameInput] = useState("");
  const [communityDescriptionInput, setCommunityDescriptionInput] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [settingsLoadingKey, setSettingsLoadingKey] = useState(null);
  const [accessChecked, setAccessChecked] = useState(
    !prefetchedCommunity || prefetchedCommunity?.isPausedByOwner === true,
  );

  const breadcrumbs = useMemo(
    () => [
      { label: "MySpace", path: "/myspace" },
      {
        label: "Communities",
        path: communitiesSnapshot ? null : "/communities",
        onClick: communitiesSnapshot
          ? () => navigate("/communities", { state: communitiesSnapshot })
          : undefined,
      },
      { label: community?.name || "Community", path: null },
    ],
    [communitiesSnapshot, community, navigate],
  );

  const loadData = React.useCallback(async () => {
    try {
      if (!prefetchedCommunity?.isPausedByOwner) {
        setAccessChecked(false);
      }
      if (!prefetchedCommunity) {
        setLoading(true);
      }
      const communityRes = await communitiesAPI.getCommunity(communityId);
      const nextCommunity = communityRes.data.community;

      if (nextCommunity?.isPausedByOwner) {
        setCommunity(nextCommunity);
        setCommunityNameInput(nextCommunity?.name || "");
        setCommunityDescriptionInput(nextCommunity?.description || "");
        setLessons([]);
        setMembers([]);
        setInvites([]);
        setPendingRequests([]);
        setIsEditingCommunity(false);
        setAccessChecked(true);
        return;
      }

      const [lessonsRes, membersRes] = await Promise.all([
        communitiesAPI.getCommunityLessons(communityId),
        communitiesAPI.getCommunityMembers(communityId),
      ]);

      setCommunity(nextCommunity);
      setCommunityNameInput(nextCommunity?.name || "");
      setCommunityDescriptionInput(nextCommunity?.description || "");
      setIsEditingCommunity(false);
      setLessons(lessonsRes.data.lessons || []);
      setMembers(membersRes.data.activeMembers || []);
      setInvites(
        nextCommunity?.canManage
          ? membersRes.data.invites || []
          : [],
      );
      setPendingRequests(
        nextCommunity?.canManage
          ? membersRes.data.pendingRequests || []
          : [],
      );
      setAccessChecked(true);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to load community",
        "error-warning",
      );
      navigate("/communities");
    } finally {
      setLoading(false);
    }
  }, [addToast, communityId, navigate, prefetchedCommunity]);

  const refreshMembers = React.useCallback(async () => {
    try {
      const membersRes = await communitiesAPI.getCommunityMembers(communityId);
      setMembers(membersRes.data.activeMembers || []);
      setInvites(membersRes.data.invites || []);
      setPendingRequests(membersRes.data.pendingRequests || []);
    } catch { /* silent */ }
  }, [communityId]);

  const refreshLessons = React.useCallback(async () => {
    try {
      const lessonsRes = await communitiesAPI.getCommunityLessons(communityId);
      setLessons(lessonsRes.data.lessons || []);
    } catch { /* silent */ }
  }, [communityId]);

  const scrollToPageSection = React.useCallback((sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const refreshCommunity = React.useCallback(async () => {
    try {
      const communityRes = await communitiesAPI.getCommunity(communityId);
      setCommunity(communityRes.data.community);
      setCommunityNameInput(communityRes.data.community?.name || "");
      setCommunityDescriptionInput(communityRes.data.community?.description || "");
      setIsEditingCommunity(false);
    } catch { /* silent */ }
  }, [communityId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (location.hash !== "#join-requests") return;
    document.getElementById("join-requests")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [location.hash, pendingRequests.length]);

  const handleInvite = async () => {
    if (!shareCode.trim()) {
      addToast("Share code is required", "error-warning");
      return;
    }

    try {
      setActionLoading("invite");
      await communitiesAPI.inviteByShareCode(communityId, shareCode.trim());
      addToast("Invite sent", "success-info");
      setShareCode("");
      await refreshMembers();
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to send invite",
        "error-warning",
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleCommunityEditToggle = async () => {
    if (!community?.canManage) return;

    if (isEditingCommunity) {
      if (!communityNameInput.trim()) {
        addToast("Community name is required", "error-warning");
        return;
      }

      try {
        setActionLoading("rename");
        await communitiesAPI.updateCommunity(communityId, {
          name: communityNameInput.trim(),
          description: communityDescriptionInput.trim(),
        });
        addToast("Community renamed", "success-info");
        await refreshCommunity();
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] || "Failed to rename community",
          "error-warning",
        );
      } finally {
        setActionLoading(null);
      }
      return;
    }

    setCommunityNameInput(community?.name || "");
    setCommunityDescriptionInput(community?.description || "");
    setIsEditingCommunity(true);
  };

  const handleCommunityEditCancel = () => {
    setIsEditingCommunity(false);
    setCommunityNameInput(community?.name || "");
    setCommunityDescriptionInput(community?.description || "");
  };

  const handleCopyJoinCode = async () => {
    if (!community?.joinCode) return;

    try {
      await navigator.clipboard.writeText(community.joinCode);
      addToast("Community code copied", "success-info");
    } catch {
      addToast("Failed to copy community code", "error-warning");
    }
  };

  const handleUpdateCommunitySetting = async (updates, successMessage, key) => {
    try {
      setSettingsLoadingKey(key);
      const response = await communitiesAPI.updateCommunitySettings(
        communityId,
        updates,
      );
      setCommunity((prev) => ({
        ...prev,
        ...response.data.community,
      }));
      addToast(successMessage, "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update community settings",
        "error-warning",
      );
    } finally {
      setSettingsLoadingKey(null);
    }
  };

  const handleApproveRequest = async (membershipId) => {
    try {
      setActionLoading(buildActionKey("approve-request", membershipId));
      await communitiesAPI.approveJoinRequest(communityId, membershipId);
      addToast("Join request approved", "success-info");
      await refreshMembers();
      await refreshCommunity();
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to approve join request",
        "error-warning",
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectRequest = async (membershipId) => {
    try {
      setActionLoading(buildActionKey("reject-request", membershipId));
      await communitiesAPI.rejectJoinRequest(communityId, membershipId);
      addToast("Join request rejected", "success-info");
      await refreshMembers();
      await refreshCommunity();
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to reject join request",
        "error-warning",
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleHideRequest = async (membershipId) => {
    try {
      setActionLoading(buildActionKey("hide-request", membershipId));
      await communitiesAPI.hideJoinRequest(communityId, membershipId);
      setPendingRequests((prev) =>
        prev.filter((request) => request._id !== membershipId),
      );
      addToast("Join request hidden", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to hide join request",
        "error-warning",
      );
    } finally {
      setActionLoading(null);
    }
  };

  const buildActionKey = (type, id) => `${type}:${id}`;

  const handleRequestAction = (action) => {
    setPendingAction(action);
  };

  const handleCancelAction = () => {
    setPendingAction(null);
    setActionLoading(null);
  };

  const isPendingAction = (type, id) =>
    pendingAction?.type === type && pendingAction?.id === id;

  const handleConfirmedAction = async (action) => {
    if (!action) return;

    try {
      const actionKey = buildActionKey(action.type, action.id);
      setActionLoading(actionKey);
      if (action.type === "kick") {
        await communitiesAPI.kickMember(communityId, action.id);
        setMembers((prev) => prev.filter((m) => m._id !== action.id));
        addToast("Member removed", "success-info");
        setPendingAction(null);
        refreshMembers();
        return;
      }
      if (action.type === "cancel-invite") {
        await communitiesAPI.cancelInvite(communityId, action.id);
        setInvites((prev) => prev.filter((i) => i._id !== action.id));
        addToast("Invite cancelled", "success-info");
        setPendingAction(null);
        refreshMembers();
        return;
      }
      if (action.type === "unshare") {
        await communitiesAPI.unshareLesson(communityId, action.id);
        setLessons((prev) => prev.filter((l) => l._id !== action.id));
        addToast("Lesson removed from community", "success-info");
        setPendingAction(null);
        refreshLessons();
        return;
      }
      if (action.type === "archive") {
        await communitiesAPI.archiveCommunity(communityId);
        addToast("Community archived", "success-info");
        navigate("/communities", {
          state: {
            skipFullLoad: true,
            archivedCommunities: [{ ...community, status: "archived" }],
          },
        });
        return;
      }
      if (action.type === "quit") {
        await communitiesAPI.quitCommunity(communityId);
        addToast("You left the community", "success-info");
        navigate("/communities");
        return;
      }
      setPendingAction(null);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to update community",
        "error-warning",
      );
    } finally {
      setActionLoading(null);
    }
  };

  const renderInlineConfirm = (action, confirmVariant = "danger") => {
    const isLoading =
      actionLoading === buildActionKey(action.type, action.id);

    return (
      <div
        className="community-details-inline-confirm"
        onClick={(event) => event.stopPropagation()}
      >
        <Button
          className="community-details-action-button community-details-action-button-round"
          onClick={handleCancelAction}
          disabled={isLoading}
          title="Cancel"
          aria-label="Cancel"
        >
          <X size={14} />
        </Button>
        <Button
          className={`community-details-action-button community-details-action-button-round ${
            confirmVariant === "success"
              ? "community-details-action-button-success"
              : "community-details-action-button-danger"
          }`}
          onClick={() => handleConfirmedAction(action)}
          disabled={isLoading}
          title="Confirm"
          aria-label="Confirm"
        >
          <Check size={14} />
        </Button>
      </div>
    );
  };

  const sharedLessonsSection = (
    <section
      id="community-details-lessons"
      className="community-details-shared-lessons-section"
    >
      <div className="community-details-card-header">
        <div className="community-details-card-title-group">
          <h2 className="community-details-card-title-compact">
            Shared Lessons
          </h2>
          <span>{lessons.length}</span>
        </div>
      </div>
      {lessons.length === 0 ? (
        <div className="community-details-empty">
          No lessons shared here yet.
        </div>
      ) : (
        <div className="study-lessons-grid">
          {lessons.map((lesson) => (
            <div
              key={lesson._id}
              className="study-lesson-card"
              onClick={() =>
                navigate(`/community/${communityId}/lesson/${lesson._id}`)
              }
            >
              <div
                className="study-lesson-btn community-details-lesson-link community-details-lesson-link-main"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/community/${communityId}/lesson/${lesson._id}`);
                }}
                title="View lesson"
              >
                <BookOpen size={32} />
              </div>
              <div
                className="study-lesson-btn community-details-lesson-link community-details-lesson-link-notes"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/community/${communityId}/notes/${lesson._id}`);
                }}
                title="View notes"
              >
                <StickyNote size={20} />
              </div>
              <div
                className="study-lesson-btn community-details-lesson-link community-details-lesson-link-flashcards"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/community/${communityId}/flashcards/${lesson._id}`);
                }}
                title="View flashcards"
              >
                <Layers size={20} />
              </div>
              <div className="community-details-lesson-spacer"></div>
              <div className="study-lesson-name">
                {lesson.chapter || "Untitled lesson"}
              </div>
              <div className="study-lesson-info">
                <div className="study-lesson-count">
                  {formatReadingEstimate(lesson) || lesson.author || ""}
                </div>
                <div className="study-lesson-actions">
                  {community?.canManage && (
                    <div className="community-details-action-stack">
                      {isPendingAction("unshare", lesson._id) ? (
                        renderInlineConfirm(
                          {
                            type: "unshare",
                            id: lesson._id,
                            label: lesson.chapter || "this lesson",
                          },
                          "danger",
                        )
                      ) : (
                        <button
                          className="study-lesson-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRequestAction({
                              type: "unshare",
                              id: lesson._id,
                              label: lesson.chapter || "this lesson",
                            });
                          }}
                          disabled={
                            actionLoading ===
                            buildActionKey("unshare", lesson._id)
                          }
                          title="Unshare lesson"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  const boardHasContent = Boolean(
    (community?.boardTitle || "").trim() || (community?.boardBody || "").trim(),
  );
  const boardPreviewTitle = community?.boardTitle?.trim() || "Board";
  const boardPreviewBody = community?.boardBody?.trim() || "";
  const previewInvites = invites;
  const currentUserId = user?._id || user?.id || "";
  const sortedMembers = useMemo(() => {
    const sorted = [...members].sort((a, b) => {
      if (a.role === "owner" && b.role !== "owner") return -1;
      if (a.role !== "owner" && b.role === "owner") return 1;
      return 0;
    });

    const displayNameCounts = sorted.reduce((counts, member) => {
      const displayName = member.displayName || "Member";
      counts.set(displayName, (counts.get(displayName) || 0) + 1);
      return counts;
    }, new Map());
    const seenDisplayNames = new Map();

    return sorted.map((member) => {
      const displayName = member.displayName || "Member";
      if ((displayNameCounts.get(displayName) || 0) <= 1) {
        return { ...member, displayNameLabel: displayName };
      }

      const duplicateIndex = seenDisplayNames.get(displayName) || 0;
      seenDisplayNames.set(displayName, duplicateIndex + 1);

      return {
        ...member,
        displayNameLabel:
          duplicateIndex === 0
            ? displayName
            : `${displayName} (${duplicateIndex})`,
      };
    });
  }, [members]);
  const isCurrentUserMember = (member) =>
    Boolean(currentUserId && String(member.userId) === String(currentUserId));

  const getMemberProfilePath = (member) =>
    isCurrentUserMember(member) ? "/public-profile" : `/users/${member.userId}`;
  const communityProfilePath = `/communities/${communityId}/public-profile`;

  const renderMemberProfileButton = (member) => {
    const isOwner = member.role === "owner";
    return (
      <Button
        className={`community-details-action-button community-details-action-button-round ${
          isOwner
            ? "community-details-owner-badge"
            : "community-details-member-badge"
        } ${
          isCurrentUserMember(member)
            ? "community-details-current-user-badge"
            : ""
        } ${
          member.isDeactivated
            ? "community-details-deactivated-member-badge"
            : ""
        }`}
        onClick={() => navigate(getMemberProfilePath(member))}
        title={
          isCurrentUserMember(member)
            ? "Edit your public profile"
            : "View public profile"
        }
        aria-label={isOwner ? "Owner" : "Member"}
      >
        {isOwner ? <Shield size={16} /> : <User size={16} />}
      </Button>
    );
  };

  if (loading || !accessChecked) {
    return <Spinner visible={true} />;
  }

  if (community?.isPausedByOwner) {
    return (
      <StudyLayout
        breadcrumbs={<Breadcrumbs path={breadcrumbs} />}
        buttons={<NavigationButtons />}
      >
        <div className="community-details-page">
          <section className="community-details-paused">
            <CommunityAvatar name={community?.name} />
            <div>
              <h1>Community paused</h1>
              <p>The owner self-deactivated. Content is temporarily unavailable.</p>
            </div>
          </section>
        </div>
      </StudyLayout>
    );
  }

  const communityManagementTools = community?.canManage ? (
    <div className="community-details-owner-tools">
      <div className="community-details-card community-details-square-card community-details-management-card community-details-invite-card">
        <div className="community-details-management-card-title">
          Join policy
        </div>
        <div className="community-details-policy-copy">
          <span
            className={
              community?.joinMode === "direct"
                ? "community-details-policy-pill is-direct"
                : "community-details-policy-pill is-approval"
            }
          >
            <KeyRound size={14} />
            {community?.joinMode === "direct" ? "Direct join" : "Approval required"}
          </span>
        </div>
        <div className="community-details-square-card-actions">
          <Button
            className="community-details-action-button community-details-action-button-round"
            onClick={() =>
              handleUpdateCommunitySetting(
                { joinMode: community?.joinMode === "direct" ? "approval" : "direct" },
                community?.joinMode === "direct"
                  ? "Join policy set to approval required"
                  : "Join policy set to direct join",
                "join-mode",
              )
            }
            disabled={settingsLoadingKey === "join-mode"}
            title="Toggle join policy"
            aria-label="Toggle join policy"
          >
            {community?.joinMode === "direct" ? <Lock size={16} /> : <Check size={16} />}
          </Button>
        </div>
      </div>
      <div className="community-details-card community-details-square-card community-details-management-card community-details-invite-card">
        <div className="community-details-management-card-title">
          Discover listing
        </div>
        <div className="community-details-policy-copy">
          <span
            className={
              community?.isListed
                ? "community-details-policy-pill is-listed"
                : "community-details-policy-pill"
            }
          >
            <Globe size={14} />
            {community?.isListed ? "Listed in Discover" : "Unlisted"}
          </span>
        </div>
        <div className="community-details-square-card-actions">
          <Button
            className="community-details-action-button community-details-action-button-round"
            onClick={() =>
              handleUpdateCommunitySetting(
                { isListed: !community?.isListed },
                community?.isListed
                  ? "Community removed from Discover"
                  : "Community listed in Discover",
                "listing",
              )
            }
            disabled={settingsLoadingKey === "listing"}
            title="Toggle discover listing"
            aria-label="Toggle discover listing"
          >
            {community?.isListed ? <X size={16} /> : <Globe size={16} />}
          </Button>
        </div>
      </div>
      <div className="community-details-card community-details-square-card community-details-management-card community-details-invite-card">
        <div className="community-details-management-card-title">
          Invite by member code
        </div>
        <input
          type="text"
          value={shareCode}
          onChange={(event) => setShareCode(event.target.value)}
          placeholder="Member share code"
          maxLength="6"
          className="community-details-input community-details-compact-input"
        />
        <div className="community-details-square-card-actions">
          <Button
            className="community-details-action-button community-details-action-button-round"
            onClick={handleInvite}
            disabled={actionLoading === "invite"}
            title={actionLoading === "invite" ? "Sending..." : "Invite"}
            aria-label={actionLoading === "invite" ? "Sending..." : "Invite"}
          >
            <UserPlus size={16} />
          </Button>
        </div>
      </div>
      {renderPendingInvitesSection()}
      {renderPendingRequestsSection()}
    </div>
  ) : null;
  function renderPendingInvitesSection() {
    return community?.canManage ? (
    <section className="community-details-pending-section">
      {previewInvites.length === 0 ? (
        <div className="community-details-pending-grid">
          <div className="community-details-card community-details-square-card community-details-management-card community-details-pending-empty-card">
            <div className="community-details-management-card-title">
              Pending invites
            </div>
            <div className="community-details-management-card-empty">
              No pending invites.
            </div>
          </div>
        </div>
      ) : (
        <div className="community-details-pending-grid">
          {previewInvites.map((invite) => (
            <div
              className="community-details-card community-details-square-card community-details-management-card community-details-pending-card"
              key={invite._id}
            >
              <div className="community-details-management-card-title">
                Pending Invite
              </div>
              <div className="community-details-management-invite-copy">
                <div className="community-details-management-invite-name">
                  {invite.displayName}
                </div>
                <div className="community-details-square-card-icons">
                  <span>#{invite.shareCode}</span>
                </div>
              </div>
              <div className="community-details-square-card-actions">
                <div className="community-details-action-stack">
                  {isPendingAction("cancel-invite", invite._id) ? (
                    renderInlineConfirm(
                      {
                        type: "cancel-invite",
                        id: invite._id,
                        label: invite.displayName,
                      },
                      "danger",
                    )
                  ) : (
                    <Button
                      className="community-details-action-button community-details-action-button-round community-details-action-button-danger"
                      onClick={() =>
                        handleRequestAction({
                          type: "cancel-invite",
                          id: invite._id,
                          label: invite.displayName,
                        })
                      }
                      disabled={
                        actionLoading ===
                        buildActionKey("cancel-invite", invite._id)
                      }
                      title="Cancel invite"
                      aria-label="Cancel invite"
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
    ) : null;
  }
  function renderPendingRequestsSection() {
    return community?.canManage ? (
    <section className="community-details-pending-section" id="join-requests">
      {pendingRequests.length === 0 ? (
        <div className="community-details-pending-grid">
          <div className="community-details-card community-details-square-card community-details-management-card community-details-pending-empty-card">
            <div className="community-details-management-card-title">
              Join requests
            </div>
            <div className="community-details-management-card-empty">
              No pending join requests.
            </div>
          </div>
        </div>
      ) : (
        <div className="community-details-pending-grid">
          {pendingRequests.map((request) => (
            <div
              className="community-details-card community-details-square-card community-details-management-card community-details-pending-card"
              key={request._id}
            >
              <div className="community-details-management-card-title">
                Join Request
              </div>
              <div className="community-details-management-invite-copy">
                <div className="community-details-management-invite-name">
                  {request.displayName}
                </div>
                <div className="community-details-square-card-icons">
                  <span>#{request.shareCode}</span>
                </div>
              </div>
              <div className="community-details-square-card-actions">
                <Button
                  className="community-details-action-button community-details-action-button-round community-details-action-button-muted"
                  onClick={() => handleHideRequest(request._id)}
                  disabled={
                    actionLoading === buildActionKey("hide-request", request._id)
                  }
                  title="Hide request"
                  aria-label="Hide request"
                >
                  <EyeOff size={16} />
                </Button>
                <Button
                  className="community-details-action-button community-details-action-button-round community-details-action-button-success"
                  onClick={() => handleApproveRequest(request._id)}
                  disabled={
                    actionLoading === buildActionKey("approve-request", request._id)
                  }
                  title="Approve request"
                  aria-label="Approve request"
                >
                  <Check size={16} />
                </Button>
                <Button
                  className="community-details-action-button community-details-action-button-round community-details-action-button-danger"
                  onClick={() => handleRejectRequest(request._id)}
                  disabled={
                    actionLoading === buildActionKey("reject-request", request._id)
                  }
                  title="Reject request"
                  aria-label="Reject request"
                >
                  <X size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
    ) : null;
  }
  return (
    <StudyLayout
      breadcrumbs={<Breadcrumbs path={breadcrumbs} />}
      buttons={
        <NavigationButtons
          hideActiveButton={true}
          communityRouteState={communitiesSnapshot}
        />
      }
    >
      <div
        className={`community-details-page ${
          community?.canManage ? "community-details-page-owner" : ""
        }`}
      >
        <div className="community-details-top-row">
          <section
            className={`community-details-board-preview ${
              boardHasContent ? "has-content" : ""
            } ${!boardHasContent ? "community-details-board-preview-empty" : ""}`}
            onClick={() =>
              navigate(`/communities/${communityId}/board`, {
                state: {
                  fromCommunitiesSnapshot: communitiesSnapshot,
                  prefetchedCommunity: community,
                },
              })
            }
          >
            {boardHasContent ? (
              <div className="community-details-board-preview-post">
                <div className="community-details-board-preview-post-header">
                  <span className="community-details-board-preview-icon">
                    <MessageSquareText size={16} />
                  </span>
                  <div className="community-details-board-preview-summary">
                    <h3>{boardPreviewTitle}</h3>
                    {boardPreviewBody ? (
                      <p className="community-details-board-preview-snippet">
                        {boardPreviewBody}
                      </p>
                    ) : null}
                  </div>
                  {community?.boardUpdatedAt ? (
                    <span className="community-details-board-preview-updated">
                      <span className="community-details-board-preview-divider">| </span>
                      Updated{" "}
                      {new Date(community.boardUpdatedAt).toLocaleString()}
                    </span>
                  ) : null}
                  <span className="community-details-board-preview-link">
                    <ChevronRight size={16} />
                  </span>
                </div>
              </div>
            ) : (
              <div className="community-details-board-preview-header">
                <div className="community-details-board-preview-title-group">
                  <span className="community-details-board-preview-icon">
                    <MessageSquareText size={16} />
                  </span>
                  <div>
                    <p>
                      {community?.canManage
                        ? "Create the first board message for your members."
                        : "No board message yet."}
                    </p>
                  </div>
                </div>
                <span className="community-details-board-preview-link">
                  <ChevronRight size={16} />
                </span>
              </div>
            )}
          </section>

          <div className="community-details-hero">
            <div className="community-details-top-grid">
              <div className="community-details-hero-copy">
              <div className="community-details-hero-toolbar">
                <div className="community-details-stats">
                  <span><Users size={14} /> {community?.activeMembersCount || 0}</span>
                  <span><BookOpen size={14} /> {community?.lessonsCount || 0}</span>
                </div>
                <div className="community-details-hero-actions">
                  {community?.canManage ? (
                    <>
                      {isEditingCommunity ? (
                        <div className="community-details-action-stack community-details-action-stack-down">
                          <div className="community-details-inline-confirm community-details-inline-confirm-row">
                            <Button
                              className="community-details-action-button community-details-action-button-round"
                              onClick={handleCommunityEditCancel}
                              title="Cancel rename"
                              aria-label="Cancel rename"
                            >
                              <X size={16} />
                            </Button>
                            <Button
                              className="community-details-action-button community-details-action-button-round community-details-action-button-success"
                              onClick={handleCommunityEditToggle}
                              disabled={actionLoading === "rename"}
                              title="Save rename"
                              aria-label="Save rename"
                            >
                              <Save size={16} />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Button
                            className="community-details-action-button community-details-action-button-round"
                            onClick={handleCommunityEditToggle}
                            title="Rename community"
                            aria-label="Rename community"
                          >
                            <Edit2 size={16} />
                          </Button>
                          <div className="community-details-action-stack community-details-action-stack-down">
                            {isPendingAction("archive", communityId) ? (
                              renderInlineConfirm(
                                {
                                  type: "archive",
                                  id: communityId,
                                  label: community?.name || "this community",
                                },
                                "danger",
                              )
                            ) : (
                              <Button
                                className="community-details-action-button community-details-action-button-round community-details-action-button-danger"
                                onClick={() =>
                                  handleRequestAction({
                                    type: "archive",
                                    id: communityId,
                                    label: community?.name || "this community",
                                  })
                                }
                                title="Archive community"
                                aria-label="Archive community"
                              >
                                <Archive size={16} />
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="community-details-action-stack community-details-action-stack-down">
                      {isPendingAction("quit", communityId) ? (
                        renderInlineConfirm(
                          {
                            type: "quit",
                            id: communityId,
                            label: community?.name || "this community",
                          },
                          "danger",
                        )
                      ) : (
                        <Button
                          className="community-details-action-button community-details-action-button-round community-details-action-button-danger"
                          onClick={() =>
                            handleRequestAction({
                              type: "quit",
                              id: communityId,
                              label: community?.name || "this community",
                            })
                          }
                          title="Quit community"
                          aria-label="Quit community"
                        >
                          <LogOut size={16} />
                        </Button>
                      )}
                    </div>
                  )}
                  <div className="community-details-mobile-page-nav">
                    <Button
                      className="community-details-action-button community-details-action-button-round"
                      onClick={() =>
                        scrollToPageSection("community-details-members")
                      }
                      title="Members"
                      aria-label="Members"
                    >
                      <Users size={16} />
                    </Button>
                    <Button
                      className="community-details-action-button community-details-action-button-round"
                      onClick={() =>
                        scrollToPageSection("community-details-lessons")
                      }
                      title="Lessons"
                      aria-label="Lessons"
                    >
                      <BookOpen size={16} />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="community-details-title-row">
                <CommunityAvatar
                  name={community?.name}
                  to={communityProfilePath}
                  ariaLabel={`View ${community?.name || "community"} profile`}
                />
                {isEditingCommunity ? (
                  <div className="community-details-edit-block">
                    <textarea
                      value={communityNameInput}
                      onChange={(event) => setCommunityNameInput(event.target.value)}
                      className="community-details-input community-details-edit-title"
                      rows="1"
                      maxLength="80"
                    />
                  </div>
                ) : (
                  <h1>{community?.name || "Community"}</h1>
                )}
              </div>
              {community?.canManage ? (
                <div className="community-details-inline-code">
                  <span>
                    Community code:{" "}
                    <strong>{community?.joinCode || "------"}</strong>
                  </span>
                  <Button
                    className="community-details-inline-code-copy"
                    onClick={handleCopyJoinCode}
                    title="Copy community code"
                    aria-label="Copy community code"
                  >
                    <Copy size={14} />
                  </Button>
                </div>
              ) : null}
              {!isEditingCommunity ? (
                community?.description ? (
                  <p className="community-details-description-line">
                    {community.description}
                  </p>
                ) : null
              ) : (
                <textarea
                  value={communityDescriptionInput}
                  onChange={(event) =>
                    setCommunityDescriptionInput(event.target.value)
                  }
                  className="community-details-textarea"
                  rows="3"
                  maxLength="240"
                />
              )}
              </div>
              {communityManagementTools}
            </div>
          </div>
        </div>

        <section className="community-details-management-grid">
          <div
            id="community-details-members"
            className="community-details-section community-details-members-section"
          >
            <div className="community-details-card-header">
              <div className="community-details-card-title-group">
                <h2 className="community-details-card-title-compact">
                  Members
                </h2>
                <span>{members.length}</span>
              </div>
            </div>
            <div className="community-details-card community-details-members-card">
              {members.length === 0 && (
                <div className="community-details-empty">
                  No active members yet.
                </div>
              )}
              {sortedMembers.map((member) => (
                <div
                  className={`community-details-member-row ${
                    member.isDeactivated
                      ? "community-details-member-row-deactivated"
                      : ""
                  }`}
                  key={member._id}
                >
                  <div className="community-details-row-actions community-details-row-actions-nowrap">
                    {renderMemberProfileButton(member)}
                    {member.role !== "owner" &&
                      community?.canManage &&
                      isPendingAction("kick", member._id) ? (
                      <div className="community-details-action-stack">
                        {renderInlineConfirm(
                          {
                            type: "kick",
                            id: member._id,
                            label: member.displayName,
                          },
                          "danger",
                        )}
                      </div>
                    ) : member.role !== "owner" && community?.canManage ? (
                      <div className="community-details-action-stack">
                        <Button
                          className="community-details-action-button community-details-action-button-round community-details-action-button-danger"
                          onClick={() =>
                            handleRequestAction({
                              type: "kick",
                              id: member._id,
                              label: member.displayName,
                            })
                          }
                          disabled={
                            actionLoading === buildActionKey("kick", member._id)
                          }
                          title="Kick member"
                          aria-label="Kick member"
                          >
                            <UserX size={16} />
                          </Button>
                      </div>
                    ) : null}
                  </div>
                  <div className="community-details-member-main community-details-member-inline">
                    <div className="community-details-row-title">
                      {member.displayNameLabel}
                    </div>
                    {community?.canManage && member.shareCode ? (
                      <div className="community-details-row-meta">
                        <span>#{member.shareCode}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="community-details-section community-details-secondary-column">
            <div className="community-details-subsection">
              {sharedLessonsSection}
            </div>
          </div>
        </section>
      </div>
    </StudyLayout>
  );
};

export default CommunityDetails;
